import { defineStore } from 'pinia'
import axios from 'axios'
import { useAuthStore } from './auth'
import subscriptionService from '@/services/subscriptionService'

// Define the API base URL
const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000'

export const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    currentSubscription: null,
    loading: false,
    error: null,
    plans: [
      {
        id: 'basic',
        slug: 'basic',
        name: 'Basic',
        price: 99,
        postsPerMonth: 10,
        description: 'Perfect for small businesses just starting with content marketing',
        features: [
          '10 blog posts per month',
          'SEO optimization',
          'WordPress/Drupal integration',
          'Multi-language support'
        ]
      },
      {
        id: 'standard',
        slug: 'standard',
        name: 'Standard',
        price: 349,
        postsPerMonth: 50,
        description: 'Ideal for growing businesses with regular content needs',
        features: [
          '50 blog posts per month',
          'SEO optimization',
          'WordPress/Drupal integration',
          'Multi-language support',
          'Priority scheduling'
        ]
      },
      {
        id: 'premium',
        slug: 'premium',
        name: 'Premium',
        price: 699,
        postsPerMonth: 100,
        description: 'For businesses serious about content marketing',
        features: [
          '100 blog posts per month',
          'SEO optimization',
          'WordPress/Drupal integration',
          'Multi-language support',
          'Priority scheduling',
          'Advanced analytics'
        ]
      }
    ],
    paymentHistory: []
  }),
  
  getters: {
    availablePlans: (state) => state.plans,
    currentPlan: (state) => state.currentSubscription
  },
  
  actions: {
    async fetchSubscription() {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return
      
      this.loading = true
      this.error = null
      
      try {
        console.log('Fetching subscription data from API...');
        const response = await axios.get(
          `${API_URL}/api/subscription/current`,
          {
            headers: { Authorization: `Bearer ${authStore.token}` }
          }
        )
        
        console.log('API response:', response.data);
        
        // Store the subscription data exactly as received from the API
        if (response.data.subscription) {
          this.currentSubscription = response.data;
        } else {
          console.warn('No subscription data found in API response');
          this.currentSubscription = null;
        }
        
        return true
      } catch (error) {
        console.error('Error fetching subscription:', error);
        this.error = error.response?.data?.message || 'Failed to fetch subscription'
        return false
      } finally {
        this.loading = false
      }
    },
    
    async subscribeToPlan(planId) {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return
      
      this.loading = true
      this.error = null
      
      try {
        // This would be replaced with actual API call
        const response = await axios.post(
          `${API_URL}/api/subscription/change`,
          {
            plan_id: parseInt(planId, 10)
          },
          {
            headers: { Authorization: `Bearer ${authStore.token}` }
          }
        )
        
        console.log('Subscription response:', response.data)
        
        // Return the checkout URL and session ID
        return {
          checkout_url: response.data.checkout_url,
          session_id: response.data.session_id
        }
      } catch (error) {
        console.error('Error subscribing to plan:', error)
        this.error = error.response?.data?.message || 'Subscription failed'
        return null
      } finally {
        this.loading = false
      }
    },
    
    async cancelSubscription() {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return
      
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post(
          `${API_URL}/api/subscription/cancel`,
          {},
          {
            headers: { Authorization: `Bearer ${authStore.token}` }
          }
        )
        
        // After successful cancellation, fetch the updated subscription data
        await this.fetchSubscription()
        
        return true
      } catch (error) {
        this.error = error.response?.data?.message || 'Cancellation failed'
        return false
      } finally {
        this.loading = false
      }
    },
    
    async changePlan(newPlanId) {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return
      
      this.loading = true
      this.error = null
      
      try {
        console.log('Changing plan to:', newPlanId)
        const response = await axios.post(
          `${API_URL}/api/subscription/change`,
          {
            plan_id: parseInt(newPlanId, 10) // Ensure plan_id is sent as a number
          },
          {
            headers: { Authorization: `Bearer ${authStore.token}` }
          }
        )
        
        console.log('Plan change response:', response.data)
        
        // Return the checkout URL and session ID
        return {
          checkout_url: response.data.checkout_url,
          session_id: response.data.session_id
        }
      } catch (error) {
        console.error('Error changing plan:', error)
        this.error = error.response?.data?.message || 'Plan change failed'
        return null
      } finally {
        this.loading = false
      }
    },
    
    async resumeSubscription() {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return
      
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post(
          `${API_URL}/api/subscription/resume`,
          {},
          {
            headers: { Authorization: `Bearer ${authStore.token}` }
          }
        )
        
        // Update the current plan
        if (response.data.subscription) {
          this.currentSubscription = response.data.subscription;
        }
        
        return true
      } catch (error) {
        console.error('Error resuming subscription:', error)
        this.error = error.response?.data?.message || 'Failed to resume subscription'
        return false
      } finally {
        this.loading = false
      }
    },
    
    async fetchPlans() {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) return
      
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.get(
          `${API_URL}/api/subscription/plans`,
          {
            headers: { Authorization: `Bearer ${authStore.token}` }
          }
        )
        
        // Store the plans but filter out free plans
        if (response.data.plans) {
          this.plans = response.data.plans.filter(plan => 
            plan.slug !== 'free' && plan.name !== 'Free' && parseFloat(plan.price) > 0
          );
          console.log('Fetched plans (excluding free):', this.plans);
        }
        
        return true
      } catch (error) {
        console.error('Error fetching plans:', error);
        this.error = error.response?.data?.message || 'Failed to fetch plans'
        return false
      } finally {
        this.loading = false
      }
    },
    
    // Helper method to get default description based on plan name
    getDefaultDescription(planName) {
      const descriptions = {
        'Basic': 'Perfect for small businesses just starting with content marketing',
        'Standard': 'Ideal for growing businesses with regular content needs',
        'Premium': 'For businesses serious about content marketing'
      }
      return descriptions[planName] || 'A great plan for your content needs'
    },
    
    // Helper method to get default features based on plan name and posts per month
    getDefaultFeatures(planName, postsPerMonth) {
      const baseFeatures = [
        'SEO optimization',
        'WordPress/Drupal integration',
        'Multi-language support'
      ]
      
      const planFeatures = {
        'Basic': [...baseFeatures],
        'Standard': [...baseFeatures, 'Priority scheduling'],
        'Premium': [...baseFeatures, 'Priority scheduling', 'Advanced analytics']
      }
      
      // Add the posts per month feature
      const postsFeature = `${postsPerMonth} blog posts per month`
      return [postsFeature, ...(planFeatures[planName] || baseFeatures)]
    },
    
    // Fetch payment history
    async fetchPaymentHistory() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await subscriptionService.getPaymentHistory();
        this.paymentHistory = response.payments || [];
        return true;
      } catch (error) {
        this.error = error.message || 'Failed to fetch payment history';
        return false;
      } finally {
        this.loading = false;
      }
    }
  }
}) 